// src/components/GestionRecompensas.js

import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';
import { toast } from 'react-toastify';
import Card from './Card';
import Button from './Button';
import EditarRecompensaModal from './EditarRecompensaModal';
import { FaTrash, FaPlus, FaTrophy, FaEdit } from 'react-icons/fa';

// --- Componente para el formulario de creación de recompensas ---
function FormularioRecompensa({ onRecompensaCreada }) {
  const [titulo, setTitulo] = useState('');
  const [puntos, setPuntos] = useState(50);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titulo || puntos <= 0) {
      setError('El título es obligatorio y los puntos deben ser mayor que cero.');
      return;
    }
    try {
      const nuevaRecompensa = {
        titulo,
        puntos_necesarios: puntos,
        tipo: 'FIJA', // Por ahora, solo creamos recompensas de tipo fijo
      };
      await apiClient.post('/recompensas/', nuevaRecompensa);
      toast.success('¡Recompensa creada!');
      onRecompensaCreada(); // Refresca la lista en el componente padre
      setTitulo(''); // Limpia el formulario
      setPuntos(50);
      setError('');
    } catch (err) {
      toast.error('No se pudo crear la recompensa.');
      console.error(err);
    }
  };

  return (
    <Card className="mb-8">
      <h3 className="text-xl font-bold text-principal mb-4">Crear Nueva Recompensa</h3>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 items-center">
        <input
          type="text"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ej: Ver una película"
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-principal"
        />
        <input
          type="number"
          value={puntos}
          onChange={(e) => setPuntos(e.target.value)}
          min="1"
          className="w-full sm:w-48 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-principal"
        />
        <Button type="submit" variant="accent" className="w-full sm:w-auto">
          <FaPlus className="inline mr-2" /> Añadir
        </Button>
      </form>
      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
    </Card>
  );
}

// --- Componente principal para gestionar recompensas ---
function GestionRecompensas() {
  const [recompensas, setRecompensas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [recompensaSeleccionada, setRecompensaSeleccionada] = useState(null);

  const fetchRecompensas = async () => {
    // No mostramos el loading en cada refresco para una mejor UX
    // setLoading(true); 
    try {
      const response = await apiClient.get('/recompensas/');
      setRecompensas(response.data);
    } catch (error) {
      console.error("Error al cargar las recompensas", error);
      toast.error("No se pudieron cargar las recompensas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecompensas();
  }, []);

  const handleEdit = (recompensa) => {
    setRecompensaSeleccionada(recompensa);
    setShowEditarModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta recompensa?')) {
      try {
        await apiClient.delete(`/recompensas/${id}/`);
        toast.success('Recompensa eliminada.');
        fetchRecompensas(); // Recarga la lista
      } catch (error) {
        toast.error('No se pudo eliminar la recompensa.');
      }
    }
  };

  if (loading) return <p className="text-center p-4">Cargando recompensas...</p>;

  return (
    <div>
      {showEditarModal && 
        <EditarRecompensaModal 
            recompensa={recompensaSeleccionada}
            onClose={() => setShowEditarModal(false)}
            onRecompensaActualizada={fetchRecompensas}
        />
      }

      <FormularioRecompensa onRecompensaCreada={fetchRecompensas} />
      
      <h2 className="text-2xl font-bold text-gray-700 mb-4">Recompensas Existentes</h2>
      {recompensas.length > 0 ? (
        <div className="space-y-3">
          {recompensas.map(recompensa => (
            <Card key={recompensa.id} className="flex justify-between items-center p-4">
              <div className="flex items-center gap-3">
                <FaTrophy className="text-acento h-6 w-6"/>
                <span className="font-semibold">{recompensa.titulo}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-bold text-acento">{recompensa.puntos_necesarios} Puntos</span>
                <Button variant="secondary" onClick={() => handleEdit(recompensa)}>
                  <FaEdit />
                </Button>
                <Button variant="danger" onClick={() => handleDelete(recompensa.id)}>
                  <FaTrash />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center p-6">
          <p className="text-gray-500">Aún no has creado ninguna recompensa. ¡Añade una para empezar a motivar!</p>
        </Card>
      )}
    </div>
  );
}

export default GestionRecompensas;